<template>
  <v-dialog
    data-test="app-update-dialog"
    v-model="showDialog"
    width="auto"
    persistent>
    <v-card class="pa-5">
      <v-card-title>A new version of the app has been released.</v-card-title>
      <v-card-subtitle>Press OK to reload the page.</v-card-subtitle>
      <v-card-text>
        <v-list>
          <v-list-item>Old version: {{ runningAppInfo.appVersion }}• Build: {{ runningAppInfo.buildNumber }}</v-list-item>
          <v-list-item>New version: {{ getAppVersion() }} • Build: {{ getBuildNumber() }}</v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <PrimaryButton @click="updateApp">
          OK
        </PrimaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useDocumentVisibility, useStorage, useWindowFocus } from "@vueuse/core";
import { ref, watch } from "vue";
import { getAppVersion, getBuildDate, getBuildNumber, getCommitHash } from "@/lib/appInfo";
import { PrimaryButton } from "@/components/buttons";
import logger from "@/lib/logger";

const showDialog = ref(false);

// check every time we switch to this tab
const visibility = useDocumentVisibility();
watch(visibility, (current, previous) => {
  if (current === "visible" && previous === "hidden") {
    if (shouldUpdateApp()) {
      showDialog.value = true;
    } else {
      logger.debug("App is current");
    }
  }
});

// check for application switches too
const focused = useWindowFocus();
watch(focused, (current, previous) => {
  if (current && !previous) {
    if (shouldUpdateApp()) {
      showDialog.value = true;
    } else {
      logger.debug("App is current");
    }
  }
});

// keep running version in localstorage
const runningAppInfo = useStorage(
  "appInfo",
  {
    appVersion: "",
    commitHash: "",
    buildDate: "",
    buildNumber: "",
  });

/**
 * Check if the app should reload.
 * 
 * @returns true if a new version of the app has been deployed
 */
const shouldUpdateApp = () => {
  const { buildNumber } = runningAppInfo.value;
  if (buildNumber === "") {
    runningAppInfo.value.appVersion = getAppVersion();
    runningAppInfo.value.buildDate = getBuildDate();
    runningAppInfo.value.commitHash = getCommitHash();
    runningAppInfo.value.buildNumber = getBuildNumber();
    return false;
  }
  const isCurrent = buildNumber === getBuildNumber();
  return !isCurrent;
};

/**
 * Reloads the page and updates localstorage with new appInfo
 */
const updateApp = () => {
  runningAppInfo.value.appVersion = getAppVersion();
  runningAppInfo.value.buildDate = getBuildDate();
  runningAppInfo.value.commitHash = getCommitHash();
  runningAppInfo.value.buildNumber = getBuildNumber();
  location.reload();
};

</script>
